import { CropRect } from "../../utils/videoTrim";

export type UploadSourceKind = "trimmed" | "original_fallback";

export type TeacherWebUploadDraft = {
  bucket: string;
  cdnDomain: string | null;
  cropRect: CropRect;
  durationSec: number;
  exportError: string | null;
  fileSize: number;
  idempotencyKey: string;
  linkId: number;
  mimeType: string;
  objectKey: string;
  originalFileName: string;
  originalFileSize: number;
  originalMimeType: string;
  originalVideoDurationSec: number;
  originalVideoHeight: number;
  originalVideoWidth: number;
  outputFrameCount: number;
  outputFramesPerSecond: number;
  outputVideoHeight: number;
  outputVideoWidth: number;
  region: string;
  sourceKind: UploadSourceKind;
  startTimeSec: number;
  studentUserId: number;
  teacherToken: string;
  teacherUserId: number;
  uploadedAt: string;
};

export type TeacherWebActiveAnalysisRun = {
  linkId: number;
  runId: string;
  savedAt: string;
  studentUserId: number;
};

export function createUploadIdempotencyKey(): string {
  if (typeof window.crypto.randomUUID === "function") {
    return window.crypto.randomUUID();
  }

  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, "0"));
  return [
    hex.slice(0, 4).join(""),
    hex.slice(4, 6).join(""),
    hex.slice(6, 8).join(""),
    hex.slice(8, 10).join(""),
    hex.slice(10).join(""),
  ].join("-");
}

function draftStorageKey(token: string, userId: number): string {
  return `pingpi.teacherWeb.uploadDraft.v1.${token}.${userId}`;
}

function activeRunStorageKey(token: string, userId: number): string {
  return `pingpi.teacherWeb.activeAnalysisRun.v1.${token}.${userId}`;
}

export function clearUploadDraft(token: string, userId: number): void {
  try {
    window.localStorage.removeItem(draftStorageKey(token, userId));
  } catch {
    // Some embedded browsers restrict storage. Clearing in-memory state is still enough.
  }
}

export function saveActiveAnalysisRun(
  token: string,
  activeRun: TeacherWebActiveAnalysisRun,
): void {
  try {
    window.localStorage.setItem(
      activeRunStorageKey(token, activeRun.studentUserId),
      JSON.stringify(activeRun),
    );
  } catch {
    // Keep the in-memory flow working when browser storage is unavailable.
  }
}

export function loadActiveAnalysisRun(
  token: string,
  studentUserId: number,
  linkId?: number,
): TeacherWebActiveAnalysisRun | null {
  try {
    const rawValue = window.localStorage.getItem(
      activeRunStorageKey(token, studentUserId),
    );
    if (!rawValue) return null;

    const value = JSON.parse(rawValue) as Partial<TeacherWebActiveAnalysisRun>;
    if (
      typeof value.runId !== "string"
      || !value.runId.trim()
      || value.studentUserId !== studentUserId
      || typeof value.linkId !== "number"
      || value.linkId <= 0
      || (linkId !== undefined && value.linkId !== linkId)
      || typeof value.savedAt !== "string"
    ) {
      window.localStorage.removeItem(activeRunStorageKey(token, studentUserId));
      return null;
    }

    return {
      linkId: value.linkId,
      runId: value.runId,
      savedAt: value.savedAt,
      studentUserId: value.studentUserId,
    };
  } catch {
    return null;
  }
}

export function clearActiveAnalysisRun(
  token: string,
  studentUserId: number,
  expectedRunId?: string,
): void {
  try {
    const key = activeRunStorageKey(token, studentUserId);
    if (expectedRunId) {
      const rawValue = window.localStorage.getItem(key);
      if (rawValue) {
        const value = JSON.parse(rawValue) as Partial<TeacherWebActiveAnalysisRun>;
        if (value.runId !== expectedRunId) return;
      }
    }
    window.localStorage.removeItem(key);
  } catch {
    // Some embedded browsers restrict storage. Clearing in-memory state is enough.
  }
}
